import { Button, Card, Col, Descriptions, Empty, Row, Statistic, Typography } from 'antd';
import { ArrowLeftOutlined, LinkOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import IncidentListTable from '../components/IncidentListTable';
import {
  getServiceLocation,
  listIncidentAttachments,
  listIncidents,
  listIncidentTasks,
} from '../services/api';
import type { CurrentUser, Incident, IncidentAttachment, IncidentTask, ServiceLocationDetail } from '../types/incident';

const { Text } = Typography;

interface Props {
  currentUser: CurrentUser | null;
}

const ServiceLocationDetailPage = ({ currentUser }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { serviceLocationId } = useParams();
  const [serviceLocation, setServiceLocation] = useState<ServiceLocationDetail | null>(null);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [tasksByIncident, setTasksByIncident] = useState<Record<string, IncidentTask[]>>({});
  const [attachmentsByIncident, setAttachmentsByIncident] = useState<Record<string, IncidentAttachment[]>>({});

  useEffect(() => {
    if (!serviceLocationId) return;
    let cancelled = false;
    Promise.all([
      getServiceLocation(serviceLocationId),
      listIncidents({ service_location_id: serviceLocationId }),
    ])
      .then(async ([serviceLocationItem, incidentItems]) => {
        const related = await Promise.all(
          incidentItems.map(async (incident) => {
            const [tasks, attachments] = await Promise.all([
              listIncidentTasks(incident.id).catch(() => []),
              listIncidentAttachments(incident.id).catch(() => []),
            ]);
            return { incidentId: incident.id, tasks, attachments };
          })
        );
        if (cancelled) return;
        setServiceLocation(serviceLocationItem);
        setIncidents(incidentItems);
        setTasksByIncident(Object.fromEntries(related.map((item) => [item.incidentId, item.tasks])));
        setAttachmentsByIncident(Object.fromEntries(related.map((item) => [item.incidentId, item.attachments])));
      })
      .catch(() => {
        if (!cancelled) {
          setServiceLocation(null);
          setIncidents([]);
          setTasksByIncident({});
          setAttachmentsByIncident({});
        }
      });
    return () => {
      cancelled = true;
    };
  }, [serviceLocationId]);

  const serviceLocationListPath =
    typeof location.state === 'object' &&
    location.state !== null &&
    'serviceLocationListPath' in location.state &&
    typeof location.state.serviceLocationListPath === 'string'
      ? location.state.serviceLocationListPath
      : '/service-locations';

  if (!serviceLocation) {
    return (
      <div className="page">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(serviceLocationListPath)}>
          {t('incident.detail.actions.back')}
        </Button>
        <Empty description={t('serviceLocation.detail.notFound')} />
      </div>
    );
  }

  return (
    <div className="page">
      <div className="backRow">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(serviceLocationListPath)}>
          {t('incident.detail.actions.back')}
        </Button>
      </div>
      <div className="pageMetaBar pageMetaBarRow">
        <Text type="secondary">{[serviceLocation.name, serviceLocation.code, serviceLocation.region_code, serviceLocation.organization_name].filter(Boolean).join(' / ')}</Text>
        {serviceLocation.official_url && (
          <Button icon={<LinkOutlined />} href={serviceLocation.official_url} target="_blank">
            {t('master.actions.officialUrl')}
          </Button>
        )}
      </div>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}><Card><Statistic title={t('master.stats.incidents')} value={serviceLocation.incident_count} /></Card></Col>
        <Col xs={24} md={8}><Card><Statistic title={t('master.stats.openIncidents')} value={serviceLocation.open_incident_count} /></Card></Col>
        <Col xs={24} md={8}><Card><Statistic title={t('serviceLocation.stats.serviceAssignments')} value={serviceLocation.service_assignment_count} /></Card></Col>
      </Row>
      <Card title={t('master.sections.basicInfo')}>
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
          <Descriptions.Item label={t('serviceLocation.columns.organization')}>{serviceLocation.organization_name || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('serviceLocation.columns.region')}>{serviceLocation.region_code || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('serviceLocation.columns.address')} span={2}>{serviceLocation.address || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('serviceLocation.columns.serviceOfferings')} span={2}>{serviceLocation.service_offerings || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('serviceLocation.columns.contactInfo')} span={2}>{serviceLocation.contact_info || '-'}</Descriptions.Item>
        </Descriptions>
      </Card>
      <Card title={t('serviceLocation.sections.incidents')}>
        <IncidentListTable
          incidents={incidents}
          attachmentsByIncident={attachmentsByIncident}
          tasksByIncident={tasksByIncident}
          currentUser={currentUser}
          fixedContextLabel={serviceLocation.name}
          showRoleFilters={false}
        />
      </Card>
    </div>
  );
};

export default ServiceLocationDetailPage;
